/**
 * Despacho a domicilio o retiro en el local (CHK-03).
 *
 * Las dos opciones son radios dentro de un `fieldset`: es un grupo de una sola
 * eleccion, y el teclado tiene que recorrerlo con las flechas y no con quince
 * tabulaciones. La direccion aparece SOLO con despacho — pedirle la calle a
 * quien va a retirar es fricción por nada.
 *
 * El precio de cada opcion se ve antes de elegirla, que es lo que `ENV-01` pide:
 * saber el total real antes de pagar, no despues.
 *
 * Region y comuna son dos selectores encadenados y no un texto libre
 * (`SelectorRegionComuna`): el costo puede cambiar por comuna (ADM-10).
 *
 * Se ofrecen solo las entregas que el panel deja encendidas
 * (`utilidades/entrega.ts`). Con ninguna, el checkout no llega a mostrarse: la
 * compra se cierra por WhatsApp desde el carro.
 */
import type { OpcionesDespacho } from '@elcarpintero/compartido';

import { SITIO } from '../../configuracion/sitio';
import type { DatosCheckout, ErroresCheckout } from '../../utilidades/checkout';
import { comunaElegida, regionElegida } from '../../utilidades/checkout';
import { alcanzaDespachoGratis, ofreceDespacho, ofreceRetiro } from '../../utilidades/entrega';
import { pesos } from '../../utilidades/formato';
import { Cargando } from '../comunes/EstadoPeticion';
import { Campo } from './Campo';
import { SelectorRegionComuna } from './SelectorRegionComuna';

interface Props {
  datos: DatosCheckout;
  errores: ErroresCheckout;
  opciones: OpcionesDespacho | null;
  subtotal: number;
  alCambiar: (cambio: Partial<DatosCheckout>) => void;
  alSalirDelCampo: (campo: keyof DatosCheckout) => void;
}

export function PanelEntrega({
  datos,
  errores,
  opciones,
  subtotal,
  alCambiar,
  alSalirDelCampo,
}: Props) {
  const region = regionElegida(datos, opciones);
  const comuna = comunaElegida(datos, opciones);
  const gratis = alcanzaDespachoGratis(opciones, subtotal);
  const conDespacho = ofreceDespacho(opciones);
  const conRetiro = ofreceRetiro(opciones);

  // Sin comuna elegida se muestra el costo base de la region, y sin region el
  // rango del catalogo entero: un numero siempre, para no dejar el resumen en
  // blanco mientras se completa el formulario.
  const costoDespacho = comuna?.costo ?? region?.costo ?? null;

  return (
    <section className="panel" aria-labelledby="ck-entrega">
      <div className="panel-cab">
        <h2 id="ck-entrega">Entrega</h2>
      </div>

      <div className="panel-cuerpo">
        {opciones === null ? <Cargando que="las formas de entrega" /> : null}

        {conDespacho || conRetiro ? (
          <fieldset>
            <legend className="solo-lectores">Como quieres recibir tu pedido</legend>

            {conDespacho ? (
              <label className={`opcion-envio${datos.tipoEntrega === 'despacho' ? ' sel' : ''}`}>
                <input
                  type="radio"
                  name="entrega"
                  value="despacho"
                  checked={datos.tipoEntrega === 'despacho'}
                  onChange={() => alCambiar({ tipoEntrega: 'despacho' })}
                />
                <span className="opcion-texto">
                  <span className="t">Despacho a domicilio</span>
                  <span className="d">
                    {region !== undefined
                      ? `${comuna?.plazo ?? region.plazo} · ${region.nombre}`
                      : 'Elige tu región para ver el costo y el plazo'}
                  </span>
                </span>
                <span className={`pr${gratis ? ' gratis' : ''}`}>
                  {gratis ? 'Gratis' : costoDespacho !== null ? pesos(costoDespacho) : '—'}
                </span>
              </label>
            ) : null}

            {conRetiro ? (
              <label className={`opcion-envio${datos.tipoEntrega === 'retiro' ? ' sel' : ''}`}>
                <input
                  type="radio"
                  name="entrega"
                  value="retiro"
                  checked={datos.tipoEntrega === 'retiro'}
                  onChange={() => alCambiar({ tipoEntrega: 'retiro' })}
                />
                <span className="opcion-texto">
                  <span className="t">Retiro en la sala de ventas</span>
                  <span className="d">
                    {SITIO.direccion.calle}, {SITIO.direccion.comuna} · {opciones?.plazoRetiro ?? ''}
                  </span>
                </span>
                <span className="pr gratis">Gratis</span>
              </label>
            ) : null}
          </fieldset>
        ) : null}

        {conDespacho && datos.tipoEntrega === 'despacho' ? (
          <div className="detalle-entrega">
            <SelectorRegionComuna
              prefijoId="ck"
              seccion="shipping"
              regiones={opciones?.regiones ?? []}
              comunas={region?.comunas.map((opcion) => opcion.nombre)}
              codigoRegion={datos.codigoRegion}
              comuna={datos.comuna}
              errorRegion={errores.codigoRegion}
              errorComuna={errores.comuna}
              // Cambiar de region invalida la comuna: la de antes ya no
              // pertenece a la nueva y cobraria una tarifa que no le toca.
              alCambiarRegion={(codigo) => alCambiar({ codigoRegion: codigo, comuna: '' })}
              alCambiarComuna={(nombre) => alCambiar({ comuna: nombre })}
              alSalirRegion={() => alSalirDelCampo('codigoRegion')}
              alSalirComuna={() => alSalirDelCampo('comuna')}
            />

            <div className="fila-campos">
              <Campo id="ck-calle" etiqueta="Calle" error={errores.calle}>
                {(atributos) => (
                  <input
                    {...atributos}
                    type="text"
                    autoComplete="address-line1"
                    value={datos.calle}
                    onChange={(evento) => alCambiar({ calle: evento.target.value })}
                    onBlur={() => alSalirDelCampo('calle')}
                  />
                )}
              </Campo>

              <Campo id="ck-numero" etiqueta="Número" error={errores.numero}>
                {(atributos) => (
                  <input
                    {...atributos}
                    type="text"
                    value={datos.numero}
                    onChange={(evento) => alCambiar({ numero: evento.target.value })}
                    onBlur={() => alSalirDelCampo('numero')}
                  />
                )}
              </Campo>
            </div>

            <div className="fila-campos">
              <Campo id="ck-departamento" etiqueta="Departamento u oficina (opcional)">
                {(atributos) => (
                  <input
                    {...atributos}
                    type="text"
                    autoComplete="address-line2"
                    value={datos.departamento}
                    onChange={(evento) => alCambiar({ departamento: evento.target.value })}
                  />
                )}
              </Campo>

              <Campo
                id="ck-referencia"
                etiqueta="Referencia (opcional)"
                ayuda="Un dato que le sirva a quien despacha: entre calles, color del portón."
              >
                {(atributos) => (
                  <input
                    {...atributos}
                    type="text"
                    value={datos.referencia}
                    onChange={(evento) => alCambiar({ referencia: evento.target.value })}
                  />
                )}
              </Campo>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
